import { type TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';

import {
  Sport,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { isType } from '@sorare/core/src/lib/gql';

import { RecommendedPlayers } from 'components/onboarding/ChooseTeams/RecommendedPlayers';
import { TeamsConfirmation } from 'components/onboarding/ChooseTeams/TeamsConfirmation';
import { TeamsSelection } from 'components/onboarding/ChooseTeams/TeamsSelection';

import type {
  ChooseTeams_card,
  ChooseTeams_so5Leaderboard,
  addRecommendedLineupToCartMutation,
  addRecommendedLineupToCartMutationVariables,
} from './__generated__/index.graphql';

const cardFragment = gql`
  fragment ChooseTeams_card on Card {
    slug
    ...RecommendedPlayers_card
  }
  ${RecommendedPlayers.fragments.card}
` as TypedDocumentNode<ChooseTeams_card>;

const ADD_RECOMMENDED_CARDS_TO_CART_MUTATION = gql`
  mutation addRecommendedLineupToCartMutation(
    $input: addRecommendedLineupToCartInput!
  ) {
    addRecommendedLineupToCart(input: $input) {
      cart {
        id
        mainCartItems {
          id
          card {
            slug
            ...ChooseTeams_card
          }
        }
      }
      errors {
        message
        code
      }
    }
  }
  ${cardFragment}
` as TypedDocumentNode<
  addRecommendedLineupToCartMutation,
  addRecommendedLineupToCartMutationVariables
>;

enum Steps {
  SELECTION,
  CONFIRMATION,
  RECOMMENDED_PLAYERS,
}
type OnboardingGame = ChooseTeams_so5Leaderboard['so5League']['games'][number];
type OnboardingTeam =
  | NonNullable<OnboardingGame['homeTeam']>
  | NonNullable<OnboardingGame['awayTeam']>;

type Props = {
  so5Leaderboard?: ChooseTeams_so5Leaderboard;
  loading?: boolean;
  onSubmit: () => void;
};
export const ChooseTeams = ({ so5Leaderboard, loading, onSubmit }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const [selectedTeams, setSelectedTeams] = useState<OnboardingTeam[]>([]);
  const [recommendedCards, setRecommendedCards] = useState<ChooseTeams_card[]>(
    []
  );
  const [step, setStep] = useState(Steps.SELECTION);

  const [addRecommendedLineupToCart] = useMutation(
    ADD_RECOMMENDED_CARDS_TO_CART_MUTATION,
    {
      showErrorsWithSnackNotification: true,
    }
  );

  const { so5League } = so5Leaderboard || {};

  const components = {
    [Steps.SELECTION]: (
      <TeamsSelection
        so5League={so5League}
        loading={loading}
        onSubmit={teams => {
          setSelectedTeams(teams);
          setStep(Steps.CONFIRMATION);
        }}
      />
    ),
    [Steps.CONFIRMATION]: (
      <TeamsConfirmation
        selectedTeams={selectedTeams}
        onSubmit={async () => {
          const { data: mutationData } = await addRecommendedLineupToCart({
            variables: {
              input: {
                sport: Sport.FOOTBALL,
                amount: {
                  amount: '25',
                  currency:
                    currentUser?.userSettings.fiatCurrency ||
                    SupportedCurrency.USD,
                },
                teamSlugs: selectedTeams.map(({ slug }) => slug),
                so5LeaderboardSlug: so5Leaderboard?.slug || '',
              },
            },
          });
          setRecommendedCards(
            mutationData?.addRecommendedLineupToCart?.cart?.mainCartItems
              .map(({ card }) => card)
              .filter(r => isType(r, 'Card')) || []
          );
          setStep(Steps.RECOMMENDED_PLAYERS);
        }}
      />
    ),
    [Steps.RECOMMENDED_PLAYERS]: (
      <RecommendedPlayers
        so5Fixture={so5League?.so5Fixture}
        recommendedCards={recommendedCards}
        onSubmit={onSubmit}
      />
    ),
  };

  return components[step];
};

ChooseTeams.fragments = {
  anyCard: cardFragment,
  so5Leaderboard: gql`
    fragment ChooseTeams_so5Leaderboard on So5Leaderboard {
      slug
      id
      so5League {
        slug
        so5Fixture {
          slug
          ...RecommendedPlayers_so5Fixture
        }
        games {
          id
          homeTeam {
            slug
            ...TeamsConfirmation_teamInterface
          }
          awayTeam {
            slug
            ...TeamsConfirmation_teamInterface
          }
        }
        ...TeamsSelection_so5League
      }
    }
    ${TeamsSelection.fragments.so5League}
    ${RecommendedPlayers.fragments.so5Fixture}
    ${TeamsConfirmation.fragments.teamInterface}
  ` as TypedDocumentNode<ChooseTeams_so5Leaderboard>,
};
